import * as Sentry from '@sentry/vue';

import { parseUserInfo } from '~/utils/user_info';

import type { Me } from '~/utils/user_info';
import type { UserInfo } from '~/schemas/user_info_schema';

type NuxtAppWithProfileRequest = ReturnType<typeof useNuxtApp> & {
  _profileRequest?: Promise<void>;
};

/**
 * プロフィール（GetProfile）をアプリ全体で共有する。
 *
 * @remarks
 * ヘッダー（全ページ）・ページ本体・各コンポーネントがそれぞれ
 * `GqlGetProfile()` を呼んでいたため、1 画面で最大 3 回発行されていた。
 * 取得結果を `useState` に集約し、1 画面につき 1 回だけ発行する。
 *
 * GraphQL の取得エラーは呼び出し側に伝播させる（従来どおり各画面で扱う）。
 * パースエラーのみ Sentry に送って `userInfo` を null にする。
 */
export const useProfile = () => {
  /** GetProfile のレスポンスそのもの */
  const me = useState<Me | null>('profile:me', () => null);
  /** パース済みのプロフィール。パースできなかった場合は null */
  const userInfo = useState<UserInfo | null>('profile:user_info', () => null);
  const isLoaded = useState('profile:is_loaded', () => false);

  // 進行中のリクエストは nuxtApp に持たせる。
  // useState と違い payload に載らないため Promise を保持できる。
  const nuxtApp = useNuxtApp() as NuxtAppWithProfileRequest;

  const fetchProfile = async () => {
    const data = await GqlGetProfile();
    me.value = data.me ?? null;

    try {
      userInfo.value = data.me ? parseUserInfo(data.me) : null;
    } catch (e) {
      // プロフィールが不完全でも画面自体は表示できるようにする
      Sentry.captureException(e);
      userInfo.value = null;
    }

    isLoaded.value = true;
  };

  const startFetch = () => {
    nuxtApp._profileRequest = fetchProfile().finally(() => {
      nuxtApp._profileRequest = undefined;
    });
    return nuxtApp._profileRequest;
  };

  /**
   * プロフィールを取得する。取得済みの場合は再取得しない。
   *
   * @param options.force 取得済み・取得中に関わらず取り直す
   */
  const loadProfile = async (options: { force?: boolean } = {}) => {
    if (options.force) {
      await startFetch();
      return;
    }

    if (isLoaded.value) return;

    // 同一画面の複数コンポーネントから同時に呼ばれても 1 リクエストに束ねる
    await (nuxtApp._profileRequest ?? startFetch());
  };

  /**
   * 共有しているプロフィールを破棄する。
   *
   * @remarks
   * ログアウト時に呼ぶ。破棄しないと、状態が残ったまま画面遷移した場合に
   * 直前の利用者の情報が表示されうる。
   */
  const clearProfile = () => {
    me.value = null;
    userInfo.value = null;
    isLoaded.value = false;
    nuxtApp._profileRequest = undefined;
  };

  return {
    me,
    userInfo,
    isLoaded,
    loadProfile,
    /** プロフィール更新後など、明示的に取り直したいとき */
    refreshProfile: () => loadProfile({ force: true }),
    clearProfile,
  };
};
